import { DownloadOutlined } from '@ant-design/icons';
import { Button, message, Modal, Spin, SpinProps, Tooltip } from 'antd';
import ButtonGroup from 'antd/lib/button/button-group';
import { RcFile } from 'antd/lib/upload';
import Dragger from 'antd/lib/upload/Dragger';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { PriceBookEntryImport, PriceBookEntryLabels, PriceBookEntryPayload, priceBookEntryImportSchema } from 'models/PriceBookEntry';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addErrorItem, setImportPriceBookEntries, setImportTotal } from 'redux/slices/importSlice';
import { useAppDispatch } from 'redux/store';
import * as xlsx from 'xlsx';
import { ValidationError } from 'yup';

const headerLabelToField: Record<string, keyof PriceBookEntryImport> = Object.entries(PriceBookEntryLabels).reduce(
  (map, [field, label]) => ({
    ...map,
    [label.toLowerCase()]: field as keyof PriceBookEntryImport,
    [field.toLowerCase()]: field as keyof PriceBookEntryImport
  }),
  {} as Record<string, keyof PriceBookEntryImport>
);

const requiredFields: (keyof PriceBookEntryImport)[] = ['productNumber', 'condition', 'type', 'listPrice', 'unitPrice', 'discountPercentage', 'effectiveDate'];

const parseExcelDate = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    return new Date(Math.round((value - 25569) * 86400 * 1000)).toLocaleDateString();
  }

  return String(value);
};

export const ImportPriceBookModal: React.FC = () => {
  const { id: entityId } = useParams();
  const [isOpen, _setIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const [isLoading, _setIsLoading] = useState(false);
  const navigate = useNavigate();

  const importLoadingConfig: SpinProps = {
    indicator: (
      <div style={{ width: 50, marginLeft: -25 }}>
        <BlockLoader direction="loader loader--slideUp" />
      </div>
    ),
    spinning: isLoading,
    wrapperClassName: isLoading ? 'list-loading' : ''
  };

  const onOpen = (): void => _setIsOpen(true);
  const onClose = (): void => _setIsOpen(false);

  const handleImport = async (file: RcFile | Blob): Promise<void> => {
    if (!entityId) {
      message.error('No entity Id!');

      return;
    }

    if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      message.error('You can only import files with the extension .xlsx');

      return;
    }

    _setIsLoading(true);
    const buffer = await file.arrayBuffer();
    const workbook = xlsx.read(buffer, { type: 'buffer' });

    for (const sheetName of workbook.SheetNames) {
      const rawRows = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 }) as unknown[][];

      if (!rawRows.length) continue;

      const headerRow = (rawRows[0] as unknown[]).map((h) =>
        String(h ?? '')
          .trim()
          .toLowerCase()
      );
      const columnMap: Record<number, keyof PriceBookEntryImport> = {};

      for (let i = 0; i < headerRow.length; i++) {
        const field = headerLabelToField[headerRow[i]];

        if (field) columnMap[i] = field;
      }

      const missingFields = requiredFields.filter((f) => !Object.values(columnMap).includes(f));

      if (missingFields.length) {
        message.error(`Spreadsheet is missing required columns: ${missingFields.join(', ')}`);
        _setIsLoading(false);

        return;
      }

      const itemsToCreate: PriceBookEntryPayload[] = [];

      for (const rawRow of rawRows.slice(1)) {
        const cells = rawRow as unknown[];
        const sheetRow = Object.entries(columnMap).reduce((row, [colIndex, field]) => ({ ...row, [field]: cells[Number(colIndex)] }), {} as PriceBookEntryImport);
        const effectiveDate = parseExcelDate(sheetRow.effectiveDate);
        const endDate = parseExcelDate(sheetRow.endDate);
        const sheetRowWithDates = { ...sheetRow, effectiveDate, endDate: endDate || null };

        try {
          await priceBookEntryImportSchema.validate(sheetRowWithDates);

          itemsToCreate.push({
            productNumber: String(sheetRowWithDates.productNumber).trim(),
            condition: String(sheetRowWithDates.condition).trim(),
            type: String(sheetRowWithDates.type).trim(),
            listPrice: Number(sheetRowWithDates.listPrice),
            unitPrice: Number(sheetRowWithDates.unitPrice),
            discountPercentage: Number(sheetRowWithDates.discountPercentage),
            effectiveDate: sheetRowWithDates.effectiveDate,
            endDate: sheetRowWithDates.endDate
          });
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          if (error instanceof ValidationError) {
            dispatch(addErrorItem({ productNumber: String(sheetRow.productNumber || 'Unknown'), errors: error.errors }));
          } else if ('data' in error) {
            dispatch(addErrorItem({ productNumber: String(sheetRow.productNumber || 'Unknown'), errors: [error.data] }));
          } else {
            dispatch(addErrorItem({ productNumber: String(sheetRow.productNumber || 'Unknown'), errors: [error.message] }));
          }
        }
      }

      dispatch(setImportTotal(itemsToCreate.length));
      dispatch(setImportPriceBookEntries(itemsToCreate));

      _setIsLoading(false);
      navigate(`/business/${entityId}/price-book/import`);
    }
  };

  return (
    <>
      <ButtonGroup>
        <Tooltip title="Import price book entries from a spreadsheet">
          <Button onClick={onOpen}>Import Price Book</Button>
        </Tooltip>
        <Tooltip title="Download import template">
          <Button download href="/import-price-book-template.xlsx">
            <DownloadOutlined />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <Modal visible={isOpen} onCancel={onClose} title="Import Price Book Entries" footer={[]}>
        <Spin spinning={isLoading} indicator={importLoadingConfig.indicator} tip="Preparing Import">
          <Dragger
            showUploadList={false}
            customRequest={({ file }): void => {
              if (typeof file !== 'string') handleImport(file);
            }}>
            Upload (xlsx)
          </Dragger>
        </Spin>
      </Modal>
    </>
  );
};
